import {
    ComponentEvent,
    ContentDataTypes,
    CurationData,
    DataLayer,
    DataLayerEvent,
    DataLayerEventName,
    GeolocationCurationData,
    GtmEvent,
    ListingData,
    toBreachBaseEventShape,
    toCookieConsentEventShape,
    toSubscribeBaseEventShape,
    toSubscribeCompleteEventShape,
} from '@news-mono/web-common'
import debug from 'debug'
import {
    getLoggedInStatus,
    toGtmShape,
} from '../../user-registration/user-analytics/user-analytics'

export const dataLayerDebug = debug('data-layer')

export interface Properties {
    [key: string]: any
}

declare const dataLayer: DataLayer
declare const googleTagManager: any

let previousEventVariables: { [key: string]: undefined } = {}
let currentPageVariables: { [key: string]: undefined } = {}
let stickyLocationVariable = ''
const defaultVariables = {}

// ED-215: GA365 events used to have stale properties mapped to 'undefined' as this suited our setup at the time.
// Going forward with our migration to GA4, our external consultants have requested we delete these stale props entirely.
const deleteStaleValues = <T extends Properties>(obj: T) => {
    type Mapped = { [K in keyof T]: undefined }
    return Object.keys(obj).reduce((acc: any) => {
        delete obj[acc]
    }, {} as any)
}

export const resetForTests = () => {
    previousEventVariables = {}
    currentPageVariables = {}
}

/**
 * GTM aggregates existing values that are included on each event
 * This function resets all variables to allow us to start clean for each page
 */
export const resetDataLayerProperties = (gtmId: string) => {
    if (typeof googleTagManager === 'undefined') {
        return
    }
    const gtm = googleTagManager[gtmId]
    if (!gtm || !gtm.dataLayer || typeof gtm.dataLayer.reset !== 'function') {
        return
    }
    gtm.dataLayer.reset()
}

/**
 * Accepts a datalayer event type and raises it to google data layer. handlePageEventsInDatalayer
 * implements this internaly.
 */
export const raiseDataLayerEvent = (event: DataLayerEvent | undefined) => {
    try {
        if (!event || typeof dataLayer === 'undefined') {
            return
        }

        let variablesToReset = previousEventVariables

        if (event.resetPageVariables) {
            variablesToReset = {
                ...previousEventVariables,
                ...currentPageVariables,
            }
            currentPageVariables = defaultVariables
        }

        const eventToPush = {
            ...(event.doNotIncludePreviousEventVariables
                ? {}
                : variablesToReset),
            event: event.event,
            ...event.eventVariables,
            ...event.pageVariables,
            ...event.permanentVariables,
        }

        dataLayerDebug('DataLayer event raised: %o', eventToPush)

        if (event.eventVariables?.ecommerce) {
            dataLayer.push({ ecommerce: null })
        }
        dataLayer.push(eventToPush)

        // store the current location, if not breachscreen modal
        const currentLocation = (eventToPush as any).location
        if (currentLocation) {
            if (
                currentLocation === 'breachscreen modal' &&
                stickyLocationVariable !== ''
            ) {
                dataLayer.push({ location: stickyLocationVariable })
            } else {
                stickyLocationVariable = currentLocation
            }
        }

        if (event.eventVariables) {
            previousEventVariables = deleteStaleValues(event.eventVariables)
        }

        if (event.pageVariables) {
            currentPageVariables = currentPageVariables
                ? {
                      ...currentPageVariables,
                      ...deleteStaleValues(event.pageVariables),
                  }
                : deleteStaleValues(event.pageVariables)
        }
    } catch (err) {
        console.error('Failed to raise GTM event', err)
        return
    }
}

export interface CollectionContext {
    timestamp: number
    componentType: string
    componentRenderPath: string
    dataDefinition: ContentDataTypes
    collectionCardCount: number
}

export interface ViewItemListData {
    item_list_category: string
    item_list_category2: string
    item_list_id: string
    item_list_name: string
    component_path: string
}

/**
 * This function returns the appropriate contextual data given the data type thats passed through
 * @param contextObject {CollectionContext}
 * Context object that we get from the componentOnEventMiddleware
 */
export const returnContextualData = (
    contextObject: CollectionContext,
): ViewItemListData | undefined => {
    const getCategory = ():
        | {
              item_list_category: string
              item_list_category2?: string
          }
        | undefined => {
        switch (contextObject.dataDefinition.type) {
            case 'context':
                return {
                    item_list_category:
                        contextObject.dataDefinition.section || '',
                }
            case 'curation':
                return {
                    item_list_category: contextObject.dataDefinition.name,
                }
            case 'listing':
                return {
                    item_list_category: contextObject.dataDefinition.topics[0],
                }
            case 'active-promotion':
                return {
                    item_list_category:
                        contextObject.dataDefinition.promotionSlotId,
                }
            case 'news-ticker':
            case 'geolocation':
                return {
                    item_list_category: 'curation',
                }
            case 'promotional':
                return {
                    item_list_category:
                        contextObject.dataDefinition.curationId ||
                        contextObject.dataDefinition.metadataKey,
                }

            case 'video-listing':
                return {
                    item_list_category: contextObject.dataDefinition.topics[0],
                }
            case 'notification-centre':
                return {
                    item_list_category: 'notification-centre',
                    item_list_category2: 'notification-centre',
                }
            case 'aggregated': {
                const sourceLength = contextObject.dataDefinition.sources.length

                const countAndCategory =
                    contextObject.dataDefinition.sources.reduce(
                        (
                            prevVal,
                            curVal,
                        ): {
                            item_list_category: string
                        } => {
                            // Aggregated can hold listing/curation/geo-location
                            const getCountAndCategory = (
                                dataType:
                                    | CurationData
                                    | ListingData
                                    | GeolocationCurationData,
                            ) =>
                                dataType.type === 'geolocation'
                                    ? {
                                          count: dataType.pageSize,
                                          category: dataType.type,
                                      }
                                    : dataType.type === 'curation'
                                    ? {
                                          count: dataType.pageSize,
                                          category: dataType.name,
                                      }
                                    : {
                                          count: dataType.paging.pageSize,
                                          category: dataType.topics[0],
                                      }

                            const currentCountAndCategory =
                                getCountAndCategory(curVal)

                            const prevListCategory = prevVal.item_list_category
                            const isSame =
                                prevListCategory ===
                                currentCountAndCategory.category

                            return {
                                item_list_category: isSame
                                    ? prevListCategory
                                    : prevListCategory +
                                      (prevListCategory !== '' ? ', ' : '') +
                                      currentCountAndCategory.category,
                            }
                        },
                        {
                            item_list_category: '',
                        },
                    )
                return countAndCategory
            }
            default:
                return
        }
    }

    const firstCategory = getCategory()

    if (!firstCategory) {
        return
    }

    const itemOffset =
        contextObject.componentRenderPath.match(/\/main\/\[(\d+)\]/)

    return {
        ...{
            item_list_category2: contextObject.dataDefinition.type,
            item_list_id: toItemListId(contextObject.componentType),
            item_list_name: contextObject.componentType,
            component_path: contextObject.componentRenderPath,
        },
        ...firstCategory,
    }
}

/**
 * toDataLayerEvent is a generic kind of function and will accept an event type
 * which allows you to maintain type saftey.
 */
export const toDataLayerEvent = (
    event: GtmEvent,
    actualPageVariables: any,
): DataLayerEvent | undefined => {
    switch (event.type) {
        case DataLayerEventName.collectionAvailable: //'collection.available':
            return {
                event: event.type,
                eventVariables: {
                    collection: {
                        // // If there is context use it
                        ...(event as any).context,
                        ...event.payload,
                    },
                },
            }

        case DataLayerEventName.removeFromCart: // remove_from_cart
        case DataLayerEventName.subscriptionPromptCheckout: // subscription_prompt_checkout
        case DataLayerEventName.viewCart: // view_cart
            return {
                event: event.type,
                eventVariables: {
                    ecommerce: event.payload,
                },
            }

        case DataLayerEventName.viewItemList: {
            //view_item_list
            if (event.payload.cardInformation.length === 0) {
                return
            }

            const context = (event as any)
                .context as unknown as CollectionContext

            // This breaks if context.dataDefinition is undefined, which is the case from some Edition routes.
            const contextualData =
                context.dataDefinition && returnContextualData(context)

            // We need to splice the contexualData into the items array
            event.payload.cardInformation = event.payload.cardInformation.map(
                (cardInfo: any) => {
                    return {
                        ...cardInfo,
                        ...{
                            item_list_category:
                                contextualData?.item_list_category,
                            item_list_category2:
                                contextualData?.item_list_category2,
                            item_list_id: contextualData?.item_list_id,
                            item_list_name: contextualData?.item_list_name,
                            component_path: contextualData?.component_path,
                            price: 0.0,
                        },
                    }
                },
            )

            return {
                event: event.type,
                doNotIncludePreviousEventVariables: true,
                eventVariables: {
                    ecommerce: {
                        count: event.payload.cardInformation.length,
                        currency: 'AUD',
                        items: event.payload.cardInformation,
                    },
                },
            }
        }

        case DataLayerEventName.selectItemEvent: {
            const context = (event as any)
                .context as unknown as CollectionContext

            const contextualData = returnContextualData(context)
            const { payload } = event

            return {
                event: event.type,
                eventVariables: {
                    link_text: payload.link_text,
                    link_url: payload.link_url,
                    ecommerce: {
                        currency: 'AUD',
                        items: [
                            {
                                index: payload.index,
                                item_brand: payload.item_brand,
                                item_category: payload.item_category,
                                item_category2: payload.item_category2,
                                item_id: payload.item_id,
                                item_list_category:
                                    contextualData?.item_list_category,
                                item_list_category2:
                                    contextualData?.item_list_category2,
                                item_list_id: contextualData?.item_list_id,
                                item_list_name: contextualData?.item_list_name,
                                component_path: contextualData?.component_path,
                                item_name: payload.item_name,
                                item_variant: payload.item_variant,
                                price: 0.0,
                            },
                        ],
                    },
                },
            }
        }

        case DataLayerEventName.cardClicked: //'card.clicked':
            if (
                typeof event.payload.cardItem !== 'string' &&
                event.payload.cardItem.cardType === 'publication'
            ) {
                return {
                    event: DataLayerEventName.cardClicked, //'card.clicked',
                    eventVariables: {
                        card: {
                            id: event.payload.cardItem.id,
                            byline: event.payload.cardItem.byline || 'unknown',
                            contentType:
                                event.payload.fundingType || 'non-sponsored',
                            title: event.payload.cardItem.shortHeadline,
                            href: event.payload.cardItem.link,
                            cardFormat: event.payload.cardFormat,
                        },
                        collection: {
                            [`card${event.payload.cardNumber}Info`]:
                                event.payload.cardInfo,

                            // If there is context use it
                            ...(event as any).context,
                        },
                    },
                }
            }

            if (
                typeof event.payload.cardItem !== 'string' &&
                event.payload.cardItem.cardType === 'breaking-news'
            ) {
                return {
                    event: DataLayerEventName.cardClicked, //'card.clicked',
                    eventVariables: {
                        card: {
                            title: event.payload.cardItem.shortHeadline,
                            href: event.payload.cardItem.link,
                            cardFormat: event.payload.cardFormat,
                        },
                        collection: {
                            [`card${event.payload.cardNumber}Info`]:
                                event.payload.cardInfo,

                            // If there is context use it
                            ...(event as any).context,
                        },
                    },
                }
            }

            return {
                event: DataLayerEventName.cardClicked, //'card.clicked',
                eventVariables: {
                    card: {
                        cardFormat: event.payload.cardFormat,
                    },
                    collection: {
                        [`card${event.payload.cardNumber}Info`]:
                            event.payload.cardInfo,

                        // If there is context use it
                        ...(event as any).context,
                    },
                },
            }
        case 'page-load-started':
            return {
                event: DataLayerEventName.syntheticPageInitiated, //'syntheticPage.initiated',
                resetPageVariables: true,
                pageVariables: {
                    ...event.payload,
                    location: event.payload.location.pathname,
                },
            }
        case 'page-load-failed':
            return {
                event: DataLayerEventName.syntheticPageError, //'syntheticPage.error'
                eventVariables: {
                    ...event.payload,
                    location: event.payload.location.pathname,
                },
            }
        case 'page-load-complete':
            return {
                event: DataLayerEventName.syntheticPageCompleted, //'syntheticPage.completed',
                pageVariables: {
                    ...event.payload,
                    ...actualPageVariables, // overwrite stale pageVariable data
                    location: event.payload.location.pathname,
                },
            }
        case DataLayerEventName.bandViewable: //'band-viewable'
            return {
                event: DataLayerEventName.viewabilityBandViewed, //'viewability.band.viewed'
                eventVariables: event.payload,
            }

        case DataLayerEventName.adClick: //'ad.click':
        case DataLayerEventName.adPlay: //'ad.play':
        case DataLayerEventName.adPause: //'ad.pause':
        case DataLayerEventName.adRequest: //'ad.request':
        case DataLayerEventName.adError: //'ad.error':
        case DataLayerEventName.adLoad: // 'ad.load':
        case DataLayerEventName.adStart: // 'ad.start':
        case DataLayerEventName.adMilestone25: //'ad.milestone.25':
        case DataLayerEventName.adMilestone50: //'ad.milestone.50':
        case DataLayerEventName.adMilestone75: //'ad.milestone.75':
        case DataLayerEventName.adEnd: //'ad.end':
        case DataLayerEventName.playerReady: //'player.ready':
        case DataLayerEventName.playerError: //'player.error':
        case DataLayerEventName.playerUserActive: //'player.user.active':
        case DataLayerEventName.playerUserInactive: //'player.user.inactive':
        case DataLayerEventName.playerFullscreenEnter: //'player.fullscreen.enter':
        case DataLayerEventName.playerFullscreenExit: //'player.fullscreen.exit':
        case DataLayerEventName.videoMilestone25: //'video.milestone.25':
        case DataLayerEventName.videoMilestone50: //'video.milestone.50':
        case DataLayerEventName.videoMilestone75: //'video.milestone.75':
        case DataLayerEventName.videoStickyOn: //'video.sticky.on':
        case DataLayerEventName.videoStickyOff: //'video.sticky.off':
        case DataLayerEventName.videoAvailable: //'video.available':
        case DataLayerEventName.videoStart: //'video.start':
        case DataLayerEventName.videoEnd: //'video.end':
        case DataLayerEventName.countdownInactivityFinished: //'countdowninactivity.finished':
        case DataLayerEventName.shareButtonClick: //'shareButton.click':
        case DataLayerEventName.galleryThumbnailClick: //'gallery.thumbnail.click':
        case DataLayerEventName.galleryThumbnailsShow: //'gallery.thumbnails.show':
        case DataLayerEventName.galleryThumbnailsHide: //'gallery.thumbnails.hide':
        case DataLayerEventName.galleryLoadMore: //'gallery.load-more':
        case DataLayerEventName.linkClicked: //'link.clicked':
        case DataLayerEventName.newsletterSignupAvailable: //'newsletterSignup.available':
        case DataLayerEventName.newsletterSignupInteract: //'newsletterSignup.interact':
        case DataLayerEventName.newsletterSignupSuccess: //'newsletterSignup.success':
        case DataLayerEventName.newsletterSignupFail: //'newsletterSignup.fail':
        case DataLayerEventName.adunitViewable: //'adunit.viewable':
        case DataLayerEventName.adunitLoaded: //'adunit.loaded':
        case DataLayerEventName.navClicked: //'nav.clicked':
        case DataLayerEventName.navHidden: //'nav.hidden',
        case DataLayerEventName.navFocused: // 'nav.focused',
        case DataLayerEventName.scrollToTop: // 'scroll.toTop',
        case DataLayerEventName.surveyBannerViewed: // 'survey_banner.viewed',
        case DataLayerEventName.surveyBannerLinkClick: // 'survey_banner.link_clicked',
        case DataLayerEventName.surveyBannerDismissed: // 'survey_banner.dismissed',
        case DataLayerEventName.extendedAccess: // extended-access.event
        case DataLayerEventName.subscribeWithGoogle: //subscribe-with-google.event
            return {
                event: event.type,
                eventVariables: event.payload,
            }

        case DataLayerEventName.spaInitialised:
            return {
                event: DataLayerEventName.spaInitialised, //'spa.initialised',
                permanentVariables: {
                    enabledFeatures: event.payload.enabledFeatures.join(','),
                    adBlocked: event.payload.adBlockEnabled ? 'True' : 'False',
                },
            }
        case DataLayerEventName.navAvailable: //'nav.available':
            return {
                event: event.type,
                eventVariables: {
                    navName: event.originator,
                    ...event.payload,
                },
            }

        case DataLayerEventName.authLogin: //'auth.login':
            return {
                event: event.type,
                pageVariables: toGtmShape({
                    entitlements: event.payload.entitlements,
                    wanUserId: event.payload.wanUserId,
                    auth0UserId: event.payload.auth0UserId,
                    isLoggedIn: true,
                    loginProvider: event.payload.loginProvider,
                    occupantId: event.payload.occupantId,
                    socialProviders: event.payload.socialProviders,
                    subscriptionId: event.payload.subscriptionId,
                    subscriptionType: event.payload.subscriptionType,
                }),
            }

        case DataLayerEventName.authLoginRefresh: //'auth.loginRefresh':
            return {
                event: event.type,
                pageVariables: toGtmShape({
                    entitlements: event.payload.entitlements,
                    wanUserId: event.payload.wanUserId,
                    auth0UserId: event.payload.auth0UserId,
                    isLoggedIn: true,
                    loginProvider: event.payload.loginProvider,
                    occupantId: event.payload.occupantId,
                    socialProviders: event.payload.socialProviders,
                    subscriptionId: event.payload.subscriptionId,
                    subscriptionType: event.payload.subscriptionType,
                }),
            }

        case DataLayerEventName.authLogout: //'auth.logout':
            return {
                event: event.type,
                pageVariables: {
                    loggedInStatus: getLoggedInStatus(false),
                },
            }
        case DataLayerEventName.breachScreenAvailable: //'breachscreen.available':
            return {
                event: event.type,
                eventVariables: toBreachBaseEventShape(event.payload),
            }
        case DataLayerEventName.articleAvailable: {
            // 'view_item'
            const itemObj = { ...event.payload }
            const context = (event as any)
                .context as unknown as CollectionContext

            // set item list name & id
            itemObj['item_list_name'] = context.componentType
            itemObj['item_list_id'] = toItemListId(context.componentType)
            itemObj['component_path'] = context.componentRenderPath

            return {
                event: 'view_item',
                resetPageVariables: true,
                eventVariables: {
                    ecommerce: { items: [itemObj] },
                },
            }
        }

        case DataLayerEventName.breachScreenClick: //'breachscreen.click':
            return {
                event: event.type,
                eventVariables: toBreachBaseEventShape(event.payload), // the shape of this event is supposed to match breachscreen.available (DPT-1390)
            }
        case DataLayerEventName.subscribeComplete: //'subscribe.complete':
            return {
                event: event.type,
                eventVariables: toSubscribeCompleteEventShape(event.payload),
            }
        case DataLayerEventName.subscribeClick: //'subscribe.click':
            return {
                event: event.type,
                eventVariables: toSubscribeBaseEventShape(event.payload),
            }
        case DataLayerEventName.addToCart: // ga4: add_to_cart
            return {
                event: event.type,
                resetPageVariables: true,
                eventVariables: {
                    ecommerce: {
                        lead_depth: 1,
                        ...event.payload,
                    },
                },
            }
        case DataLayerEventName.viewSubscriptionItemListEvent:
            return {
                event: event.parentType,
                resetPageVariables: true,
                eventVariables: {
                    lead_depth: 1,
                    location: 'breachscreen modal',
                    ecommerce: {
                        currency: 'AUD',
                        count: event.payload.items.length,
                        items: event.payload.items,
                    },
                },
            }
        case DataLayerEventName.cookieConsent: //'cookie.consent':
            return {
                event: event.type,
                eventVariables: toCookieConsentEventShape(event.payload),
            }
        case DataLayerEventName.radioAvailable:
        case DataLayerEventName.radioPlay:
        case DataLayerEventName.radioStop:
        case DataLayerEventName.radio30:
        case DataLayerEventName.radioEnd:
            return {
                event: event.type,
                eventVariables: {
                    name: event.originator,
                    ...event.payload,
                },
            }
        case DataLayerEventName.subscriberDropdown: //'subscriberDropdown.click':
        case DataLayerEventName.commentsAvailable:
        case DataLayerEventName.commentPost:
        case DataLayerEventName.commentReact:
        case DataLayerEventName.commentReplied:
        case DataLayerEventName.commentReport:
        case DataLayerEventName.commentEdit:
        case DataLayerEventName.commentLoadMore:
        case DataLayerEventName.commentIgnore:
        case DataLayerEventName.commentError:
        case DataLayerEventName.pollieRatingSubmit:
        case DataLayerEventName.loadMoreOlderPosts:
        case DataLayerEventName.webStoryClicked:
        case DataLayerEventName.genericArticleBannerViewed:
        case DataLayerEventName.webStoryCarouselViewed:
        case DataLayerEventName.milestoneClick:
        case DataLayerEventName.search:
        case DataLayerEventName.searchFilter:
        case DataLayerEventName.searchResults:
        case DataLayerEventName.savedArticlesResults:
        case DataLayerEventName.savedArticlesSave:
        case DataLayerEventName.savedArticlesUnsave:
        case DataLayerEventName.savedArticlesFilter:
        case DataLayerEventName.moreButtonClick:
        case DataLayerEventName.notificationCentreOpened:
        case DataLayerEventName.notificationCentreClosed:
            return {
                event: event.type,
                eventVariables: event.payload,
            }

        case DataLayerEventName.oddsLinkPressed:
            return {
                event: event.type,
                eventVariables: event.payload,
            }
        default:
            return
    }
}

/**
 * handlePageEventsInDatalayer allows you to create a handler function to raise events to data layer.
 * It accepts a mapping function that you can use to map product specific events to data layer event types.
 * Internally this implements raiseDataLayerEvent for you. This function is also a generic, so pass in an
 * event type so you can maintain type saftey.
 */
export const handlePageEventsInDatalayer = <
    Event extends ComponentEvent<Properties>,
>(
    mapEventData: (
        event: Event,
        actualPageVariables: any,
    ) => DataLayerEvent | undefined,
) => {
    return (event: Event, actualPageVariables: any) => {
        try {
            const dataLayerEventToRaise = mapEventData(
                event,
                actualPageVariables,
            )

            if (dataLayerEventToRaise) {
                return raiseDataLayerEvent(dataLayerEventToRaise)
            }
        } catch (err) {
            console.error({ err, event }, 'Failed to raise page event')
        }
    }
}

export const toItemListId = (itemListName: string): string => {
    const replaceSpace = / /gi
    const replaceDash = /-/gi

    return itemListName
        .toLowerCase()
        .replace(replaceSpace, '_')
        .replace(replaceDash, '_')
}
